import React from 'react';
import { useTranslation } from 'next-i18next';
import { Card, CardContent } from '@gu-corp/gu-ui';
import { TierGemIcon, type GemTier } from './tier-gem-icon';
import { formatJocAmount } from '~/constants/joc-club';

/**
 * Tier ladder for the JOC Club home right column. Thresholds mirror the
 * on-chain tiers (WHITE_MIN … PLATINUM_MIN) — the contract is the source of
 * truth, so this stays honest about what a given holding unlocks.
 */
interface LadderTier {
  key: GemTier;
  min: number;
}

// Highest first (as shown top-to-bottom).
const LADDER: LadderTier[] = [
  { key: 'platinum', min: 1_000_000 },
  { key: 'gold', min: 50_000 },
  { key: 'silver', min: 5_000 },
  { key: 'bronze', min: 1_000 },
  { key: 'white', min: 1 },
];

/** The highest ladder tier the holding qualifies for (null if below White). */
function currentLadderTier(totalJoc: number): GemTier | null {
  return LADDER.find((t) => totalJoc >= t.min)?.key ?? null;
}

interface TierLadderProps {
  totalJoc: number;
  className?: string;
}

export const TierLadder: React.FC<TierLadderProps> = ({ totalJoc, className }) => {
  const { t } = useTranslation('common');
  const current = currentLadderTier(totalJoc);

  return (
    <Card className={className}>
      <CardContent className="p-4">
        <p className="mb-3 text-sm font-bold">{t('joc_club.tier_ladder')}</p>
        <ul className="space-y-0.5">
          {LADDER.map((tier) => {
            const isCurrent = tier.key === current;
            return (
              <li
                key={tier.key}
                className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${
                  isCurrent ? 'bg-primary/10' : ''
                }`}
              >
                <TierGemIcon tier={tier.key} className="h-[18px] w-[18px] shrink-0" />
                <span className={`text-sm ${isCurrent ? 'font-bold' : 'text-muted-foreground'}`}>
                  {t(`joc_club.labels.tiers.${tier.key}`)}
                </span>
                {isCurrent && (
                  <span className="flex items-center gap-1 text-xs font-medium text-primary">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    {t('joc_club.current')}
                  </span>
                )}
                <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                  {formatJocAmount(tier.min)} JOC
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

export default TierLadder;
